import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from "react";
import { FaCheck } from "react-icons/fa";
import { MdArrowOutward, MdContentCopy, MdFullscreen, MdFullscreenExit, MdOutlineInfo } from "react-icons/md";
import resizeIcon from "../../../../../assets/images/pip/resize.svg";
import type { PipCopyItem, SetupPipContent } from "../../../constants/setupSteps.constants";
import InfoBanner from "../../common/InfoBanner/InfoBanner";
import styles from "./SetupPip.module.scss";

export interface PipSize {
  width: number;
  height: number;
}

export interface SetupPipProps {
  content: SetupPipContent;
  blockedUrl: string | null;
  openLabel: string;
  onOpenBlocked: (view: Window) => void;
  onDone: () => void;
}

const VIDEO_RATIO = 16 / 9;
const DEFAULT_VIDEO_HEIGHT = 300;
export const INITIAL_VIDEO_HEIGHT = Math.round(DEFAULT_VIDEO_HEIGHT * 0.7);
const EXPANDED_VIDEO_HEIGHT = 540;
const MIN_VIDEO_HEIGHT = 160;
const MAX_VIDEO_HEIGHT = 720;
const SIDE_PADDING = 16;
const HEADER_HEIGHT = 40;
const GAP = 8;
const ROW_HEIGHT = 34;
const DONE_HEIGHT = 32;
const BASE_FONT_SIZE = 16;
const MIN_SCALE = 0.6;
const MAX_SCALE = 2.5;

const baseSizeOf = (content: SetupPipContent): PipSize => ({
  width: Math.round(DEFAULT_VIDEO_HEIGHT * VIDEO_RATIO) + SIDE_PADDING,
  height: HEADER_HEIGHT + GAP + DEFAULT_VIDEO_HEIGHT + GAP + content.rows.length * (ROW_HEIGHT + GAP) + DONE_HEIGHT + GAP,
});

export const pipSizeOf = (content: SetupPipContent, videoHeight = DEFAULT_VIDEO_HEIGHT): PipSize => {
  const base = baseSizeOf(content);
  const scale = videoHeight / DEFAULT_VIDEO_HEIGHT;
  return { width: Math.round(base.width * scale), height: Math.round(base.height * scale) };
};

const scaleOf = (view: Window, content: SetupPipContent): number => {
  const base = baseSizeOf(content);
  const scale = Math.min(view.innerWidth / base.width, view.innerHeight / base.height);
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
};

const clampVideoHeight = (height: number): number => Math.min(MAX_VIDEO_HEIGHT, Math.max(MIN_VIDEO_HEIGHT, Math.round(height)));

const viewOf = (event: MouseEvent<HTMLElement>): Window => event.currentTarget.ownerDocument.defaultView ?? window;

const outerSizeOf = (view: Window): PipSize => ({ width: view.outerWidth, height: view.outerHeight });

const fitTo = (view: Window, size: PipSize): void => {
  view.resizeBy(size.width - view.innerWidth, size.height - view.innerHeight);
};

const PipCopyButton = ({ item }: { item: PipCopyItem }): JSX.Element => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1000);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = (event: MouseEvent<HTMLButtonElement>): void => {
    viewOf(event).navigator.clipboard.writeText(item.value).then(() => setCopied(true)).catch(() => undefined);
  };

  return (
    <button type="button" className={styles.copy} title={item.value} aria-label={/^copy\b/i.test(item.label) ? item.label : `Copy ${item.label}`} onClick={copy}>
      <span className={styles.copyLabel}>{item.label}</span>
      {copied ? <FaCheck aria-hidden="true" className={styles.copyIcon} /> : <MdContentCopy aria-hidden="true" className={styles.copyIcon} />}
    </button>
  );
};

const SetupPip = ({ content, blockedUrl, openLabel, onOpenBlocked, onDone }: SetupPipProps): JSX.Element => {
  const [videoHeight, setVideoHeight] = useState(DEFAULT_VIDEO_HEIGHT);
  const drag = useRef<{ y: number; height: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const restore = useRef<PipSize | null>(null);
  const contentRef = useRef(content);
  contentRef.current = content;
  const rowCount = content.rows.length;

  useEffect(() => {
    const doc = rootRef.current?.ownerDocument;
    const view = doc?.defaultView;
    if (!doc || !view) return undefined;
    const applyScale = (): void => {
      const scale = scaleOf(view, contentRef.current);
      doc.documentElement.style.fontSize = `${BASE_FONT_SIZE * scale}px`;
      setVideoHeight(Math.round(DEFAULT_VIDEO_HEIGHT * scale));
    };
    applyScale();
    view.addEventListener("resize", applyScale);
    return () => view.removeEventListener("resize", applyScale);
  }, [rowCount]);

  const resizeVideo = (view: Window, height: number): void => {
    fitTo(view, pipSizeOf(content, height));
    setVideoHeight(height);
  };

  const openBlocked = (event: MouseEvent<HTMLButtonElement>): void => {
    if (!blockedUrl) return;
    onOpenBlocked(viewOf(event));
  };

  const toggleSize = (event: MouseEvent<HTMLButtonElement>): void => {
    const view = viewOf(event);
    if (!expanded) {
      restore.current = outerSizeOf(view);
      resizeVideo(view, EXPANDED_VIDEO_HEIGHT);
      setExpanded(true);
      return;
    }
    const original = restore.current;
    restore.current = null;
    setExpanded(false);
    if (!original) {
      resizeVideo(view, DEFAULT_VIDEO_HEIGHT);
      return;
    }
    view.resizeTo(original.width, original.height);
  };

  const startDrag = (event: PointerEvent<HTMLButtonElement>): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { y: event.screenY, height: videoHeight };
  };

  const endDrag = (event: PointerEvent<HTMLButtonElement>): void => {
    const start = drag.current;
    drag.current = null;
    if (!start || event.screenY === start.y) return;
    restore.current = null;
    setExpanded(false);
    resizeVideo(viewOf(event), clampVideoHeight(start.height + event.screenY - start.y));
  };

  return (
    <div className={styles.pip} ref={rootRef}>
      <header className={styles.header}>
        <span className={styles.title}>{content.title}</span>
        <div className={styles.headerActions}>
          {blockedUrl ? (
            <button type="button" className={styles.openLink} onClick={openBlocked}>
              {openLabel}
              <MdArrowOutward aria-hidden="true" />
            </button>
          ) : null}
          <button type="button" className={styles.iconButton} aria-label={expanded ? "Shrink" : "Enlarge"} onClick={toggleSize}>
            {expanded ? <MdFullscreenExit aria-hidden="true" /> : <MdFullscreen aria-hidden="true" />}
          </button>
        </div>
      </header>
      {blockedUrl ? (
        <InfoBanner variant="informative" icon={<MdOutlineInfo />} className={styles.blockedNote}>
          Click on <button type="button" className={styles.noteLink} onClick={openBlocked}>{openLabel}</button> to continue.
        </InfoBanner>
      ) : null}

      <div className={styles.body}>
        <div className={styles.media}>
          {content.video ? (
            <video key={content.video} className={styles.video} src={content.video} autoPlay muted loop playsInline />
          ) : null}
        </div>

        {content.rows.map((row) => (
          <div className={styles.row} key={row.map((item) => item.label).join("|")}>
            {row.map((item) => <PipCopyButton key={item.label} item={item} />)}
          </div>
        ))}

        <div className={styles.footer}>
          <button type="button" className={styles.done} onClick={onDone}>I’ve Done This</button>
        </div>
      </div>

      <button
        type="button"
        className={styles.resize}
        data-tip="Drag to expand"
        aria-label="Drag to expand"
        onPointerDown={startDrag}
        onPointerUp={endDrag}
        onPointerCancel={() => { drag.current = null; }}
      >
        <img src={resizeIcon} alt="" aria-hidden="true" />
      </button>
    </div>
  );
};

export default SetupPip;
